import { TableBody, TableCell, TableContainer, TableRow as MuiTableRow } from "@material-ui/core";
import MuiTable from "@material-ui/core/Table";
import useText from "constants/text";
import React from "react";
import { getTableSorting, makeSearch, stablilizeTableSorting } from "utils";
import { Action } from "./Actions";
import { TableFooter } from "./Footer";
import { TableHeader } from "./Header";
import { TableLoader } from "./Loader";
import { FieldGetters, TableRow } from "./Row";
import { EmptyTableContent, EmptyText, useTableStyles } from "./styles";

export interface Column<RowData> {
  title: React.ReactNode;
  field: string;
  render?(data: RowData): React.ReactNode;
}

type Order = "asc" | "desc";

export interface TableProps<RowData, RowDataKey extends keyof RowData> {
  onRowClick?(data: RowData, event?: React.MouseEvent<unknown, MouseEvent>): void;
  onRowDelete?(data: RowData): void;
  onRowEdit?(data: RowData): void;
  onOrderEdit?(data: RowData): void;
  setRowsPerPage?(length: number): void;
  onRowChecked?(data: string[]): void;
  onRowCheckedRow?(data: string[]): void;
  onChangePage?(event: unknown, newPage: number): void;
  rowsIdentifier: RowDataKey;
  data: RowData[];
  columns: Column<RowData>[];
  isLoading?: boolean;
  hasSelection?: boolean;
  rowScanned?: string[];
  hasAllSelection?: boolean;
  orderByIdentifier?: RowDataKey;
  defaultSort?: Order;
  rowsPerPageOptions?: number[];
  searchTerm?: string;
  columnsToSearchIn?: string[];
  autoChecked?: boolean;
  isDisabledChecked?: boolean;
  selectedRows?: string[];
  disabled?: boolean;
  getters?: FieldGetters;
  currentPage?: number;
  countTotal?: number;
  rowsPerPage?: number;
  page?: number;
  multipleActions?: Action[];
  onRowMenuClick?(key: string, row: any): void;
  tableWidth?: string;
  tableHeight?: string;
  isDisabledFooter?: boolean;
  isOrderMenu?: boolean;
  notShowAction?: (row: RowData) => boolean;
  hideHeader?: boolean;
}

export function Table<RowData, RowDataKey extends keyof RowData>({
  columns = [],
  isLoading = false,
  hasSelection = false,
  rowScanned,
  hasAllSelection = true,
  onRowClick,
  rowsIdentifier,
  onRowDelete,
  onRowEdit,
  onOrderEdit,
  data = [],
  onRowChecked,
  onRowCheckedRow,
  orderByIdentifier,
  rowsPerPageOptions,
  defaultSort = "asc",
  searchTerm,
  columnsToSearchIn,
  autoChecked,
  selectedRows,
  isDisabledChecked,
  isDisabledFooter = false,
  getters,
  onChangePage,
  currentPage,
  countTotal,
  rowsPerPage,
  setRowsPerPage,
  multipleActions,
  onRowMenuClick,
  isOrderMenu = false,
  tableWidth = "99%",
  tableHeight = "100%",
  notShowAction,
  hideHeader = false,
}: TableProps<RowData, RowDataKey>) {
  const { text } = useText();
  const classes = useTableStyles({});
  const [selected, setSelected] = React.useState<string[]>(selectedRows || []);
  const [order, setOrder] = React.useState<Order>(defaultSort);
  const [orderBy, setOrderBy] = React.useState<keyof RowData>(orderByIdentifier);
  const [page, setPage] = React.useState(0);
  React.useEffect(() => {
    if (selectedRows) setSelected(selectedRows);
  }, [selectedRows]);

  const handlerSelectAll = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (hasAllSelection) {
        if (event.target.checked) {
          const newSelecteds = data.map((currentCell: any) => currentCell[rowsIdentifier]);
          setSelected(newSelecteds);
          onRowChecked && onRowChecked(newSelecteds);
          onRowCheckedRow && onRowCheckedRow(newSelecteds);
        } else {
          setSelected([]);
          onRowChecked && onRowChecked([]);
          onRowCheckedRow && onRowCheckedRow([]);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, onRowChecked, onRowCheckedRow, rowsIdentifier]
  );

  const handleRowSelectClick = React.useCallback(
    (event: React.MouseEvent<unknown>, id: string) => {
      const selectedIndex = selected.indexOf(id);

      let newSelected: string[] = [];

      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selected, id);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selected.slice(1));
      } else if (selectedIndex === selected.length - 1) {
        newSelected = newSelected.concat(selected.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
      }
      onRowChecked && onRowChecked(newSelected);
      onRowCheckedRow && onRowCheckedRow(newSelected);
      (!selectedRows || !onRowChecked || !onRowCheckedRow) && setSelected(newSelected);
    },
    [onRowChecked, onRowCheckedRow, selected]
  );

  const totalRowsSelectedCount = React.useMemo(() => selected.length, [selected]);
  const totalDataToShowCount = React.useMemo(() => data?.length || -1, [data]);
  const isRowSelected = React.useCallback(
    (name: any) => {
      return selected.includes(name);
    },

    [selected]
  );
  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof RowData) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  let rows: any[] | null = null;
  rows = React.useMemo(() => data, [data]);

  const filterRows = React.useMemo(
    () => (searchTerm ? makeSearch(data, searchTerm, columnsToSearchIn) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, searchTerm]
  );
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  const isUserFiltering = Boolean(searchTerm) && rows;

  const dataToShow = isUserFiltering ? filterRows : rows;
  const isDataToShowNotEmpty = dataToShow && Array.isArray(dataToShow) && dataToShow?.length !== 0;

  const sortedRows = React.useMemo(() => {
    let result;
    if (countTotal) {
      result =
        isDataToShowNotEmpty &&
        stablilizeTableSorting(dataToShow, getTableSorting(order, orderBy)).slice(0, rowsPerPage);
    } else {
      result =
        isDataToShowNotEmpty &&
        stablilizeTableSorting(dataToShow, getTableSorting(order, orderBy)).slice(
          page * rowsPerPage,
          page * rowsPerPage + rowsPerPage
        );
    }

    return result;
  }, [dataToShow, order, orderBy, page, rowsPerPage, isDataToShowNotEmpty]);

  function selectAllCheck() {
    return rowScanned && rowScanned.length > 0 && totalDataToShowCount === rowScanned.length;
  }

  return (
    <>
      <TableContainer>
        <MuiTable size="medium" aria-label="customized table" stickyHeader className={classes.table}>
          {!hideHeader && (
            <TableHeader
              columns={columns}
              onRequestSort={handleRequestSort}
              hasSelection={hasSelection}
              asAction
              onAllRowChecked={handlerSelectAll}
              rowCount={totalDataToShowCount}
              selectedRowsCount={totalRowsSelectedCount}
              orderBy={orderBy}
              isDisabledChecked={selectAllCheck() ? false : isDisabledChecked}
              order={order}
            />
          )}

          {isLoading ? (
            <TableLoader rowDepth={rowsPerPage} cellDepth={columns.length + 1} />
          ) : (
            <TableBody>
              {isDataToShowNotEmpty &&
                sortedRows?.map((rowContent, index) => (
                  <TableRow
                    key={`${rowContent[rowsIdentifier]}${index}`}
                    columns={columns}
                    rowContent={rowContent as any}
                    rowsIdentifier={rowsIdentifier}
                    hasSelection={hasSelection}
                    rowScanned={rowScanned}
                    onRowEdit={onRowEdit}
                    onRowDelete={onRowDelete}
                    onRowClick={onRowClick}
                    onRowChecked={handleRowSelectClick}
                    onRowCheckedRow={handleRowSelectClick}
                    selectRowsCount={totalRowsSelectedCount}
                    rowsCount={totalDataToShowCount}
                    isRowSelected={isRowSelected}
                    autoChecked={autoChecked}
                    isDisabledChecked={isDisabledChecked}
                    getters={getters}
                    multipleActions={multipleActions}
                    onRowMenuClick={onRowMenuClick}
                    isOrderMenu={isOrderMenu}
                    onOrderEdit={onOrderEdit}
                    rowColor={rowContent?.color}
                    notShowAction={notShowAction}
                  />
                ))}
              {!isDataToShowNotEmpty && searchTerm && (
                <MuiTableRow>
                  <TableCell colSpan={columns.length + 1}>
                    <EmptyTableContent>
                      {text.all.no_result_found} <i className="highlighted">{searchTerm}</i>
                    </EmptyTableContent>
                  </TableCell>
                </MuiTableRow>
              )}
            </TableBody>
          )}
        </MuiTable>
        {data.length === 0 && <EmptyText> {text.all.empty_table} </EmptyText>}
      </TableContainer>
      {isDataToShowNotEmpty && !isDisabledFooter && (
        <TableFooter
          rowsPerPageOptions={rowsPerPageOptions || [10]}
          handleChangePage={onChangePage || handleChangePage}
          count={countTotal || data?.length}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          page={currentPage || page}
          rowsPerPage={rowsPerPage}
        />
      )}
    </>
  );
}
